import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, from, Observable, of, throwError } from 'rxjs';
import { TrezorControllerService } from '@app/generated/services/trezor-controller.service';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import TrezorConnect, { AccountInfo, DEVICE, DEVICE_EVENT, Success, SuccessWithDevice } from '@trezor/connect-web';
import { Login } from '@trezor/connect/lib/types/api/requestLogin';
import { TrezorRegistrationDto } from '@app/generated/models/trezor-registration-dto';
import { UserSecurityControllerService } from '@app/generated/services/user-security-controller.service';
import { LoginService } from '@app/authentication/shared/services/login.service';
import { TrezorDto } from '@app/generated/models/trezor-dto';
import { TrezorOperationEnum } from '@app/authentication/shared/enums/trezor-operation.enum';
import { SuccessLoginDto } from '@app/generated/models/success-login-dto';

@Injectable({
  providedIn: 'root',
})
export class TrezorService {
  private initialized = false;
  private reloadTrezor = new BehaviorSubject<boolean>(true);
  private trezorAddressSource = new BehaviorSubject<string>('');
  private trezorData$: Observable<TrezorDto> = this.reloadTrezor.pipe(
    switchMap(() => this.trezor.getTrezorForCurrentUserUsingGet()),
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  currentAddress = this.trezorAddressSource.asObservable();

  constructor(
    private trezor: TrezorControllerService,
    private userSecurity: UserSecurityControllerService,
    private login: LoginService,
  ) {
    TrezorConnect.on(DEVICE_EVENT, (event) => {
      if (event.type === DEVICE.CONNECT) {
        const device = event.payload;
        window.sessionStorage.setItem('trezorLabel', device.label);
      }
    });
  }

  public static addTrezorDataToStorage(result: any, challengeHiddenResult: string, challengeVisualResult: string) {
    window.sessionStorage.setItem('publicKey', result.payload.publicKey);
    window.sessionStorage.setItem('signature', result.payload.signature);
    window.sessionStorage.setItem('challengeHidden', challengeHiddenResult);
    window.sessionStorage.setItem('challengeVisual', challengeVisualResult);
  }

  public static removeTrezorDataFromStorage() {
    window.sessionStorage.removeItem('publicKey');
    window.sessionStorage.removeItem('signature');
    window.sessionStorage.removeItem('challengeHidden');
    window.sessionStorage.removeItem('challengeVisual');
    window.sessionStorage.removeItem('trezorLabel');
  }

  public static getTrezorLabelFromStorage(): string | undefined {
    return window.sessionStorage.getItem('trezorLabel') || undefined;
  }

  public static getTrezorLoginFromStorage(): TrezorRegistrationDto {
    return {
      publicKey: window.sessionStorage.getItem('publicKey') || '',
      signature: window.sessionStorage.getItem('signature') || '',
      challengeHidden: window.sessionStorage.getItem('challengeHidden') || '',
      challengeVisual: window.sessionStorage.getItem('challengeVisual') || '',
    };
  }

  public static getTrezorRegistrationFromStorage(): TrezorRegistrationDto {
    return {
      ...TrezorService.getTrezorLoginFromStorage(),
      label: TrezorService.getTrezorLabelFromStorage(),
    };
  }

  /**
   * Returns observable that is true when initialization is successful and has an error if not
   */
  initializeTrezorConnect(): Observable<boolean> {
    if (this.initialized) {
      return of(true);
    }

    return this.trezor.getTrezorManifestEmailUsingGet().pipe(
      mergeMap((manifestEmail) =>
        // try to initialize trezor
        from(
          TrezorConnect.init({
            manifest: { email: manifestEmail, appUrl: 'https://coinmate.io', appName: 'Coinmate.io' },
          }),
        ).pipe(
          map(() => true),
          catchError((err) => {
            if (err.message === 'TrezorConnect has been already initialized') {
              this.initialized = true;
              return of(true);
            }
            console.log('TrezorConnect init failed', err);
            return throwError(() => new Error('TrezorConnect init failed'));
          }),
        ),
      ),
    );
  }

  private initPerform(operation: TrezorOperationEnum): Observable<[Success<Login>, string, string]> {
    return forkJoin([
      this.initializeTrezorConnect(),
      this.trezor.prepareChallengeHiddenUsingGet(),
      this.trezor.prepareChallengeVisualUsingGet({ type: operation }),
    ]).pipe(
      mergeMap(([_, challengeHidden, challengeVisual]) =>
        from(
          TrezorConnect.requestLogin({
            challengeHidden,
            challengeVisual,
          }),
        ).pipe(
          mergeMap((successLogin): Observable<[Success<Login>, string, string]> => {
            if (successLogin.success) {
              return of([successLogin, challengeHidden, challengeVisual]);
            } else {
              return throwError(() => new Error('Error in communication with Trezor.'));
            }
          }),
        ),
      ),
    );
  }

  get performLogin$(): Observable<SuccessLoginDto> {
    return this.initPerform(TrezorOperationEnum.login).pipe(
      mergeMap((result) => {
        TrezorService.addTrezorDataToStorage(result[0], result[1], result[2]);
        return this.login.loginByTrezor(TrezorService.getTrezorLoginFromStorage()).pipe(
          map((successLogin): SuccessLoginDto => successLogin),
          tap(() => {
            TrezorService.removeTrezorDataFromStorage();
            this.reloadTrezor.next(true);
          }),
        );
      }),
    );
  }

  public get trezorData(): Observable<TrezorDto> {
    return this.trezorData$;
  }

  get performRegister$(): Observable<TrezorRegistrationDto> {
    return this.initPerform(TrezorOperationEnum.register).pipe(
      mergeMap((result) => {
        const registrationData: TrezorRegistrationDto = {
          publicKey: result[0].payload.publicKey,
          signature: result[0].payload.signature,
          challengeHidden: result[1],
          challengeVisual: result[2],
          label: TrezorService.getTrezorLabelFromStorage(),
        };
        return of(registrationData);
      }),
    );
  }

  get performTwoFactorAuth$(): Observable<TrezorRegistrationDto> {
    return this.initPerform(TrezorOperationEnum.twoFactorAuth).pipe(
      mergeMap((result) => {
        return of<TrezorRegistrationDto>({
          publicKey: result[0].payload.publicKey,
          signature: result[0].payload.signature,
          challengeHidden: result[1],
          challengeVisual: result[2],
        });
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public getAddress(coin: string): string {
    this.initializeTrezorConnect().subscribe((result) => {
      const self = this;
      TrezorConnect.getAccountInfo({
        coin,
        details: 'tokens',
        // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
      }).then(function (addressResult) {
        if (addressResult.success) {
          self.trezorAddressSource.next(self.getAddressFromSuccessResult(coin, addressResult));
        } else {
          console.log(addressResult.payload.error);
        }
      });
    });

    return '';
  }

  private getAddressFromSuccessResult(coin: string, addressResult: SuccessWithDevice<AccountInfo>) {
    return coin.toLowerCase() === 'eth'
      ? addressResult?.payload?.descriptor
      : addressResult?.payload?.addresses?.unused[0].address ?? '';
  }
}
